import React, { useState } from 'react';
import './BrandAdd.css';

const countries = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'US', name: 'United States' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'RU', name: 'Russia' },
  { code: 'KR', name: 'Korea (South)' },
  { code: 'SG', name: 'Singapore' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'TR', name: 'Turkey' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'IL', name: 'Israel' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' }
];

const verticals = ['Casino', 'Sports Betting', 'Poker', 'Lottery', 'Bingo', 'Sweepstake', 'Crypto'];

const BrandAdd = () => {
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  const [formData, setFormData] = useState({
    brandName: '',
    brandUrl: '',
    selectedVerticals: [],
    selectedCountries: []
  });

  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [progressMessages, setProgressMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [urlVerified, setUrlVerified] = useState(false);
  const [verifyingUrl, setVerifyingUrl] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [detectedVerticals, setDetectedVerticals] = useState([]);

  // Reset verification when URL changes
  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setFormData({ ...formData, brandUrl: newUrl });
    setUrlVerified(false);
    setVerificationMessage('');
    setDetectedVerticals([]);
  };

  // Verify URL using DeepSeek AI
  const handleVerifyUrl = async () => {
    if (!formData.brandUrl.trim()) {
      setVerificationMessage('Please enter a URL first');
      return;
    }

    setVerifyingUrl(true);
    setVerificationMessage('');
    setDetectedVerticals([]);

    try {
      const response = await fetch(`${basePath}/api/brandAddition/verify-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: formData.brandUrl.trim() })
      });

      const data = await response.json();

      if (data.success && data.isGamblingBrand) {
        setUrlVerified(true);
        setDetectedVerticals(data.verticals || []);
        setVerificationMessage(`Verified! Detected verticals: ${data.verticals?.join(', ') || 'Unknown'}`);

        // Auto-select detected verticals
        if (data.verticals && data.verticals.length > 0) {
          const matchedVerticals = data.verticals.filter(v =>
            verticals.some(vert => vert.toLowerCase() === v.toLowerCase())
          ).map(v => verticals.find(vert => vert.toLowerCase() === v.toLowerCase()));

          if (matchedVerticals.length > 0) {
            setFormData(prev => ({
              ...prev,
              selectedVerticals: [...new Set([...prev.selectedVerticals, ...matchedVerticals])]
            }));
          }
        }
      } else {
        setUrlVerified(false);
        setVerificationMessage(data.message || 'This URL does not appear to be a gambling/betting website. Only Casino, Sports Betting, Poker, Lottery, and Bingo sites are allowed.');
      }
    } catch (err) {
      setUrlVerified(false);
      setVerificationMessage('Failed to verify URL. Please try again.');
      console.error('URL verification error:', err);
    } finally {
      setVerifyingUrl(false);
    }
  };

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleVerticalSelect = (vertical) => {
    if (!formData.selectedVerticals.includes(vertical)) {
      setFormData({
        ...formData,
        selectedVerticals: [...formData.selectedVerticals, vertical]
      });
    }
  };

  const handleRemoveVertical = (vertical) => {
    setFormData({
      ...formData,
      selectedVerticals: formData.selectedVerticals.filter(v => v !== vertical)
    });
  };

  const handleCountrySelect = (countryCode) => {
    if (!formData.selectedCountries.includes(countryCode)) {
      setFormData({
        ...formData,
        selectedCountries: [...formData.selectedCountries, countryCode]
      });
      setCountrySearch('');
      setShowCountryDropdown(false);
    }
  };

  const handleRemoveCountry = (countryCode) => {
    setFormData({
      ...formData,
      selectedCountries: formData.selectedCountries.filter(c => c !== countryCode)
    });
  };

  const isFormValid = () => {
    return formData.brandName.trim() &&
           formData.brandUrl.trim() &&
           formData.selectedVerticals.length > 0 &&
           formData.selectedCountries.length > 0 &&
           urlVerified;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setCsvFile(file);
        setError('');
      } else {
        setError('Please upload a valid CSV file');
        e.target.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setProgressMessages([]);
    setCurrentStep('');

    // Validation
    if (!isFormValid()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setCurrentStep('🚀 Connecting to server...');

    try {
      // Create FormData with all fields including CSV
      const formDataToSend = new FormData();
      formDataToSend.append('brandName', formData.brandName.trim());
      formDataToSend.append('brandUrl', formData.brandUrl.trim());
      formDataToSend.append('selectedVerticals', formData.selectedVerticals.join(','));
      formDataToSend.append('selectedCountries', formData.selectedCountries.join(','));

      // Add CSV file if uploaded
      if (csvFile) {
        formDataToSend.append('csvFile', csvFile);
      }

      setCurrentStep('📡 Sending request to server...');

      const response = await fetch(`${basePath}/api/brandAddition/add`, {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      // Handle Server-Sent Events (SSE)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            // Handle different event types
            if (data.success !== undefined) {
              // Final result
              if (data.success) {
                const { urls, merge } = data.data || {};
                let successMsg = 'Brand added successfully!';
                if (urls) {
                  successMsg += `\n• Backlinks: ${urls.backlinks || 0}`;
                  successMsg += `\n• Google URLs: ${urls.google || 0}`;
                  successMsg += `\n• Affiliate URLs found: ${urls.affiliates || 0}`;
                }
                if (merge) {
                  successMsg += `\n• Final records: ${merge.inserted || 0} inserted, ${merge.updated || 0} updated`;
                }
                setSuccess(successMsg);

                // Reset form
                setFormData({
                  brandName: '',
                  brandUrl: '',
                  selectedVerticals: [],
                  selectedCountries: []
                });
                setCsvFile(null);

                // Reset file input
                const fileInput = document.getElementById('csvFile');
                if (fileInput) fileInput.value = '';
              } else {
                const errorMsg = data.message || 'Operation failed';
                const fullError = data.stack ? `${errorMsg}\n\nStack trace:\n${data.stack}` : errorMsg;
                setError(fullError);
                console.error('Server error:', data);
              }
            } else {
              // Progress update
              const stepLabels = {
                'init': '🚀 Initializing',
                'validation': '✓ Validation',
                'step1': '📝 Step 1: Creating Brand',
                'step1_complete': '✅ Step 1: Brand Created',
                'step2': '📄 Step 2: Processing CSV Backlinks',
                'step2_complete': '✅ Step 2: Backlinks Inserted',
                'step2_skipped': '⏭️ Step 2: Skipped (No CSV)',
                'step3': '🔍 Step 3: Google Search',
                'step3_complete': '✅ Step 3: Google Search Complete',
                'step4': '🏷️ Step 4: Classifying URLs',
                'step4_complete': '✅ Step 4: URLs Classified',
                'step5_8': '📊 Steps 5-8: Fetching Enrichment Data',
                'step5_8_complete': '✅ Steps 5-8: Enrichment Complete',
                'step9': '🔀 Step 9: Merging Data',
                'step9_complete': '✅ Step 9: Merge Complete',
                'complete': '🎉 Complete!'
              };

              const displayMsg = stepLabels[data.step] || data.message;
              const detailsMsg = data.data ? ` - ${JSON.stringify(data.data)}` : '';

              setCurrentStep(displayMsg);

              // Only add to completed log if it's a completion step
              if (data.step && (data.step.includes('_complete') || data.step === 'complete' || data.step.includes('_skipped'))) {
                setProgressMessages(prev => [...prev, `${displayMsg}${detailsMsg}`]);
              }
            }
          }
        }
      }

    } catch (err) {
      const errorMessage = err.message || 'Failed to add brand';
      setError(errorMessage);
      console.error('Error adding brand:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="brand-add-container">
      <h1>Add New Brand</h1>

      <form onSubmit={handleSubmit} className="brand-add-form">
        {/* Brand Name */}
        <div className="form-group">
          <label htmlFor="brandName">Brand Name *</label>
          <input
            type="text"
            id="brandName"
            value={formData.brandName}
            onChange={(e) => setFormData({ ...formData, brandName: e.target.value })}
            placeholder="Enter brand name"
            disabled={loading}
          />
        </div>

        {/* Brand URL */}
        <div className="form-group">
          <label htmlFor="brandUrl">Brand URL *</label>
          <div className="url-input-container">
            <input
              type="text"
              id="brandUrl"
              value={formData.brandUrl}
              onChange={handleUrlChange}
              placeholder="Enter brand URL (e.g., example.com)"
              disabled={loading}
              className={urlVerified ? 'verified' : ''}
            />
            <button
              type="button"
              onClick={handleVerifyUrl}
              disabled={loading || verifyingUrl || !formData.brandUrl.trim()}
              className={`verify-btn ${urlVerified ? 'verified' : ''}`}
            >
              {verifyingUrl ? 'Verifying...' : urlVerified ? 'Verified' : 'Verify URL'}
            </button>
          </div>
          {verificationMessage && (
            <div className={`verification-message ${urlVerified ? 'success' : 'error'}`}>
              {urlVerified ? '✓' : '✗'} {verificationMessage}
            </div>
          )}
          {!urlVerified && formData.brandUrl.trim() && !verificationMessage && (
            <div className="verification-hint">
              Please verify the URL to confirm it's a gambling/betting website
            </div>
          )}
        </div>

        {/* Vertical Selector */}
        <div className="form-group">
          <label>Vertical *</label>
          <select
            onChange={(e) => {
              if (e.target.value) {
                handleVerticalSelect(e.target.value);
                e.target.value = '';
              }
            }}
            disabled={loading}
          >
            <option value="">Select vertical...</option>
            {verticals.map((vertical) => (
              <option
                key={vertical}
                value={vertical}
                disabled={formData.selectedVerticals.includes(vertical)}
              >
                {vertical}
              </option>
            ))}
          </select>

          {formData.selectedVerticals.length > 0 && (
            <div className="selected-items">
              {formData.selectedVerticals.map((vertical) => (
                <span key={vertical} className="tag tag-vertical">
                  {vertical}
                  <button
                    type="button"
                    onClick={() => handleRemoveVertical(vertical)}
                    className="tag-remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Country Selector */}
        <div className="form-group">
          <label>Country *</label>
          <div className="country-selector">
            <input
              type="text"
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              onFocus={() => setShowCountryDropdown(true)}
              placeholder="Search countries..."
              disabled={loading}
            />

            {showCountryDropdown && (
              <>
                <div className="country-dropdown">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <div
                        key={country.code}
                        onClick={() => handleCountrySelect(country.code)}
                        className={`country-option ${
                          formData.selectedCountries.includes(country.code) ? 'selected' : ''
                        }`}
                      >
                        <span className="country-flag">{country.code}</span>
                        <span className="country-name">{country.name}</span>
                      </div>
                    ))
                  ) : (
                    <div className="no-results">No countries found</div>
                  )}
                </div>
                <div
                  className="dropdown-overlay"
                  onClick={() => setShowCountryDropdown(false)}
                ></div>
              </>
            )}
          </div>

          {formData.selectedCountries.length > 0 && (
            <div className="selected-items">
              {formData.selectedCountries.map((countryCode) => {
                const country = countries.find(c => c.code === countryCode);
                return (
                  <span key={countryCode} className="tag tag-country">
                    <span className="tag-flag">{countryCode}</span>
                    {country?.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveCountry(countryCode)}
                      className="tag-remove"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* CSV Upload Section */}
        <div className="form-group csv-upload-group">
          <label htmlFor="csvFile">Backlinks CSV File (Optional)</label>
          <div className="csv-format-info">
            <p><strong>CSV Format (Tab-separated):</strong></p>
            <code>Domain ascore    Domain    Backlinks    IP Address    Country    First seen    Last seen</code>
            <p style={{ marginTop: '10px', fontSize: '12px' }}>
              Example: <code style={{ display: 'inline', padding: '2px 5px' }}>34    asgam.com    42394    162.159.135.42        2024-10-25    2025-11-18</code>
            </p>
            <p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              Note: Export this format directly from SEMrush Backlinks report
            </p>
          </div>
          <input
            type="file"
            id="csvFile"
            accept=".csv"
            onChange={handleFileChange}
            disabled={loading}
          />
          {csvFile && (
            <p className="file-info">Selected: {csvFile.name}</p>
          )}
        </div>

        {/* Submit Button */}
        <button type="submit" disabled={loading || !isFormValid()} className="submit-btn">
          {loading ? 'Processing...' : 'Add Brand'}
        </button>
      </form>

      {/* Progress Display */}
      {loading && (
        <div className="progress-container">
          <h3>Processing Brand Addition...</h3>
          {currentStep && (
            <div className="current-step">
              <span className="spinner">⏳</span> <strong>{currentStep}</strong>
            </div>
          )}
          {progressMessages.length > 0 && (
            <div className="progress-log">
              {progressMessages.map((msg, index) => (
                <div key={index} className="progress-item">
                  <span className="progress-icon">✓</span> {msg}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="success-message">
          <strong>Success:</strong> {success}
        </div>
      )}
    </div>
  );
};

export default BrandAdd;
